import {useCallback, useMemo, useState} from "react";
import styled from "@emotion/styled";
import {StopType, TimeType} from "../types";
import {Button, Icon, Text} from "@blueprintjs/core";
import {ItemRenderer, Select} from "@blueprintjs/select";
import {COLORS} from "../../../constants/constants";
import {renderStopTypeIcon} from "../icons/StopTypeIcons";
import {addMinutes, endOfMinute, startOfMinute} from "date-fns";
import StyledNumericInput from "../StyledNumericInput";
import {debounce} from "lodash";
import DropdownClearButton from "../DropdownClearButton";
import {TReadyDueFilter, TReadyDueFilterItem} from "./types";

type FilterItemProps = {
  filter: TReadyDueFilter;
  filterItem: TReadyDueFilterItem;
  onUpdate: (id: string, updatedItem: TReadyDueFilterItem, customValue: number) => void;
  onDelete: (id: string) => void;
};

type StopTypeOption = {label: string; value: StopType};
type TimeTypeOption = {label: string; value: TimeType};
type TimeValueOption = {
  label: string;
  value: string | undefined;
  customValue?: number;
  getFilterValue(customValue?: number): any;
};

const STOP_TYPE_OPTIONS: StopTypeOption[] = [
  {label: "Pickup", value: StopType.PICKUP},
  {label: "Delivery", value: StopType.DELIVERY},
  {label: "Both", value: StopType.BOTH}
];

const TIME_TYPE_OPTIONS: TimeTypeOption[] = [
  {label: "Scheduled", value: TimeType.SCHEDULED},
  {label: "Late Time", value: TimeType.LATE_TIME},
  {label: "Early Time", value: TimeType.EARLY_TIME}
];

export const TIME_VALUE_OPTIONS: TimeValueOption[] = [
  {
    label: "Now",
    value: "now",
    getFilterValue: () => {
      return [startOfMinute(new Date(0)), endOfMinute(new Date())];
    }
  },
  {
    label: "30min",
    value: "30m",
    getFilterValue: () => {
      return [startOfMinute(new Date(0)), endOfMinute(addMinutes(new Date(), 30))];
    }
  },
  {
    label: "1h",
    value: "1h",
    getFilterValue: () => {
      return [startOfMinute(new Date(0)), endOfMinute(addMinutes(new Date(), 60))];
    }
  },
  {
    label: "1h 30min",
    value: "1h30m",
    getFilterValue: () => {
      return [startOfMinute(new Date(0)), endOfMinute(addMinutes(new Date(), 90))];
    }
  },
  {
    label: "2h",
    value: "2h",
    getFilterValue: () => {
      return [startOfMinute(new Date(0)), endOfMinute(addMinutes(new Date(), 120))];
    }
  },
  {
    label: "2h 30min",
    value: "2h30m",
    getFilterValue: () => {
      return [startOfMinute(new Date(0)), endOfMinute(addMinutes(new Date(), 150))];
    }
  },
  {
    label: "3h",
    value: "3h",
    getFilterValue: () => {
      return [startOfMinute(new Date(0)), endOfMinute(addMinutes(new Date(), 180))];
    }
  },
  {
    label: "Custom",
    value: "custom",
    customValue: 0,
    getFilterValue: (customValue: number) => {
      return [startOfMinute(new Date(0)), endOfMinute(addMinutes(new Date(), customValue * 60))];
    }
  }
];

export const getCustomValueKey = (item: TReadyDueFilterItem) => {
  const {stopType, timeType, timeValue} = item;
  if (!stopType || !timeType || timeValue !== "custom") {
    return undefined;
  }
  return `${stopType}-${timeType}`.toLowerCase();
};

export const getOptionCustomValue = (
  customValues: {
    [key: string]: number;
  },
  item: TReadyDueFilterItem
) => {
  const key = getCustomValueKey(item);
  if (!key) {
    return 0;
  }
  return customValues?.[key] ?? 0;
};

export const isCustomOption = (option: TimeValueOption) => option.value === "custom";

const renderStopTypeOptionContent = (stopType: StopType | undefined) => {
  if (!stopType) return null;
  let backGroundColor;
  let textColor;
  switch (stopType) {
    case StopType.PICKUP:
      backGroundColor = "#DBEAFE";
      textColor = "#406AFF";
      break;
    case StopType.DELIVERY:
      backGroundColor = "#F0FCF3";
      textColor = "#32A467";
      break;
    case StopType.BOTH:
      backGroundColor = "#F3F4F6";
      break;
    default:
  }
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "2px",
        padding: "2px 4px",
        borderRadius: "8px",
        backgroundColor: backGroundColor,
        color: textColor,
        width: "fit-content",
        height: "100%,"
      }}
    >
      {renderStopTypeIcon(stopType, undefined, 16)}
      {STOP_TYPE_OPTIONS.find((option) => option.value === stopType)?.label}
    </div>
  );
};

const renderTimeType = (timeType: TimeType | undefined) => {
  const selectedOption = TIME_TYPE_OPTIONS.find((option) => option.value === timeType);
  if (!selectedOption) return null;
  return <Text>{selectedOption?.label}</Text>;
};

const renderTimeValue = (timeValue: string | undefined, customValue: string | undefined) => {
  const selectedOption = TIME_VALUE_OPTIONS.find((option) => option.value === timeValue);
  if (!selectedOption) return null;
  if (isCustomOption(selectedOption)) {
    return <Text>{customValue}h</Text>;
  }
  return <Text>{selectedOption?.label}</Text>;
};

const getAvailableStopTypes = (filterItems: TReadyDueFilterItem[], filterItem: TReadyDueFilterItem) => {
  const selectedTimeType = filterItem.timeType;
  if (!selectedTimeType) return STOP_TYPE_OPTIONS;
  const notAvailableStopTypes = filterItems
    .filter((item) => item.id !== filterItem.id && item.timeType === selectedTimeType)
    .map((item) => item.stopType);
  return STOP_TYPE_OPTIONS.filter((option) => !notAvailableStopTypes.includes(option.value));
};

const getAvailableTimeTypes = (filterItems: TReadyDueFilterItem[], filterItem: TReadyDueFilterItem) => {
  const selectedStopType = filterItem.stopType;
  if (!selectedStopType) return TIME_TYPE_OPTIONS;
  const notAvailableTimeTypes = filterItems
    .filter((item) => item.id !== filterItem.id && item.stopType === selectedStopType)
    .map((item) => item.timeType);
  return TIME_TYPE_OPTIONS.filter((option) => !notAvailableTimeTypes.includes(option.value));
};

const FilterItem = ({filter, filterItem, onUpdate, onDelete}: FilterItemProps) => {
  const [customValue, setCustomValue] = useState<string | undefined>(
    getOptionCustomValue(filter.customValues, filterItem).toString()
  );

  const handleStopTypeChange = useCallback(
    (selectedItem: StopTypeOption) => {
      onUpdate(filterItem.id, {...filterItem, stopType: selectedItem.value}, Number(customValue));
    },
    [customValue, filterItem, onUpdate]
  );

  const handleTimeTypeChange = useCallback(
    (selectedItem: TimeTypeOption) => {
      onUpdate(filterItem.id, {...filterItem, timeType: selectedItem.value}, Number(customValue));
    },
    [customValue, filterItem, onUpdate]
  );

  const handleTimeValueChange = useCallback(
    (selectedItem: TimeValueOption) => {
      onUpdate(
        filterItem.id,
        {
          ...filterItem,
          timeValue: selectedItem.value,
          getFilterValue: selectedItem.getFilterValue
        },
        Number(customValue)
      );
    },
    [customValue, filterItem, onUpdate]
  );

  const handleClearStopType = useCallback(() => {
    onUpdate(filterItem.id, {...filterItem, stopType: undefined}, Number(customValue));
  }, [customValue, filterItem, onUpdate]);

  const handleClearTimeType = useCallback(() => {
    onUpdate(filterItem.id, {...filterItem, timeType: undefined}, Number(customValue));
  }, [customValue, filterItem, onUpdate]);

  const handleClearTimeValue = useCallback(() => {
    onUpdate(filterItem.id, {...filterItem, timeValue: undefined}, Number(customValue));
  }, [customValue, filterItem, onUpdate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateCustomTime = useCallback(
    debounce((value: number) => {
      onUpdate(filterItem.id, filterItem, value);
    }, 500),
    [onUpdate]
  );

  const onCustomValueInputChange = useCallback(
    (valueAsNumber: number, valueAsString: string) => {
      setCustomValue(valueAsString);
      debouncedUpdateCustomTime(valueAsNumber);
    },
    [debouncedUpdateCustomTime]
  );

  const onCustomValueInputBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const parsedValue = Number(e.target.value);
    if (isNaN(parsedValue) || e.target.value === "") {
      setCustomValue("0");
    }
  }, []);

  const prepositionText = useMemo(() => {
    if (!filterItem.stopType) return "ready in next";
    return filterItem.stopType === StopType.PICKUP ? "ready in next" : "due in next";
  }, [filterItem.stopType]);

  const StopTypeOptionRenderer: ItemRenderer<StopTypeOption> = useCallback(
    (item, {handleClick}) => {
      return (
        <Option key={item.value} onClick={handleClick}>
          <div className="icon-container">
            {item.value === filterItem.stopType && <Icon icon="tick" size={13} color="#6B7280" />}
          </div>
          {renderStopTypeOptionContent(item.value)}
        </Option>
      );
    },
    [filterItem.stopType]
  );

  const TimeTypeOptionRenderer: ItemRenderer<TimeTypeOption> = useCallback(
    (item, {handleClick}) => (
      <Option key={item.value} onClick={handleClick}>
        <div className="icon-container">
          {item.value === filterItem.timeType && <Icon icon="tick" size={13} color="#6B7280" />}
        </div>
        <Text>{item.label}</Text>
      </Option>
    ),
    [filterItem.timeType]
  );

  const TimeValueOptionRenderer: ItemRenderer<TimeValueOption> = useCallback(
    (item, {handleClick}) => {
      return (
        <Option key={item.value} onClick={handleClick}>
          <div className="icon-container">
            {item.value === filterItem.timeValue && <Icon icon="tick" size={13} color="#6B7280" />}
          </div>
          {isCustomOption(item) ? (
            <>
              <StyledNumericInput
                style={{
                  width: "45px",
                  height: "24px",
                  boxShadow: "none",
                  border: "1px solid #cbd0df",
                  textOverflow: "ellipsis"
                }}
                onClick={(e) => e.stopPropagation()}
                value={customValue}
                onValueChange={onCustomValueInputChange}
                onBlur={onCustomValueInputBlur}
                onEnter={handleTimeValueChange.bind(null, item)}
                allowNegative={false}
                title={customValue && customValue?.toString().length > 3 ? customValue.toString() : undefined}
                max={99}
                stepSize={0.1}
              />
              <Text style={{paddingLeft: "4px"}}>h</Text>
            </>
          ) : (
            <Text>{item.label}</Text>
          )}
        </Option>
      );
    },
    [customValue, filterItem.timeValue, handleTimeValueChange, onCustomValueInputBlur, onCustomValueInputChange]
  );

  return (
    <StyledFilterItem>
      <StyledSelect
        items={getAvailableStopTypes(filter.items, filterItem)}
        itemRenderer={StopTypeOptionRenderer}
        onItemSelect={handleStopTypeChange}
        filterable={false}
        activeItem={null}
        popoverProps={{minimal: true, matchTargetWidth: true, enforceFocus: false, openOnTargetFocus: false}}
      >
        <SelectButton
          text={renderStopTypeOptionContent(filterItem.stopType) ?? <PlaceHolderText>Select Stop Type</PlaceHolderText>}
          endIcon={<DropdownClearButton onClear={handleClearStopType} hasValue={!!filterItem.stopType} />}
        />
      </StyledSelect>
      <StyledSelect
        items={getAvailableTimeTypes(filter.items, filterItem)}
        itemRenderer={TimeTypeOptionRenderer}
        onItemSelect={handleTimeTypeChange}
        filterable={false}
        activeItem={null}
        popoverProps={{minimal: true, matchTargetWidth: true, enforceFocus: false, openOnTargetFocus: false}}
      >
        <SelectButton
          endIcon={<DropdownClearButton onClear={handleClearTimeType} hasValue={!!filterItem.timeType} />}
          text={renderTimeType(filterItem.timeType) ?? <PlaceHolderText>Select Time Type</PlaceHolderText>}
        />
      </StyledSelect>
      <StyledPreposition>{prepositionText}</StyledPreposition>
      <StyledSelect
        items={TIME_VALUE_OPTIONS}
        itemRenderer={TimeValueOptionRenderer}
        onItemSelect={handleTimeValueChange}
        filterable={false}
        activeItem={null}
        popoverProps={{minimal: true, matchTargetWidth: true, enforceFocus: false, openOnTargetFocus: false}}
      >
        <SelectButton
          endIcon={<DropdownClearButton onClear={handleClearTimeValue} hasValue={!!filterItem.timeValue} />}
          text={
            renderTimeValue(filterItem.timeValue, customValue) ?? <PlaceHolderText>Select Time Value</PlaceHolderText>
          }
        />
      </StyledSelect>
      <DeleteButton
        onClick={onDelete.bind(null, filterItem.id)}
        icon="cross"
        variant="minimal"
        style={{borderRadius: "8px"}}
      />
    </StyledFilterItem>
  );
};

export default FilterItem;

const StyledFilterItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 80px 145px 32px;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 8px;
  background-color: #f3f4f6;
`;

const StyledSelect = styled(Select<any>)`
  width: 100%;
`;

const SelectButton = styled(Button)`
  height: 32px;
  width: 100%;
  background: ${COLORS.WHITE} !important;
  border-radius: 8px;
  border: 1px solid #cbd0df;
  box-shadow: none !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 4px 4px 8px;

  :hover {
    box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1) !important;
  }
`;

const DeleteButton = styled(Button)`
  border-radius: 8px;
`;

const StyledPreposition = styled(Text)`
  text-align: center;
`;

const Option = styled.li`
  font-family: "Roboto", sans-serif;
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 32px;
  width: 100%;
  padding: 4px;
  border-radius: 8px;
  cursor: pointer;

  :hover {
    background-color: #f3f4f6;
  }

  .icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
  }
`;

const PlaceHolderText = styled(Text)`
  color: ${COLORS.COLOR_PLACE_HOLDER};
  font-size: 13px;
`;
